import { ApiAuditLog, ApiClient, ApiDevice, ApiEmployee, BackendDeviceStatus } from './contracts';

export interface ClientViewModel {
  id: string;
  name: string;
}

export interface DeviceViewModel {
  id: string;
  deviceId: string;
  clientId: string;
  status: BackendDeviceStatus;
  statusLabel: string;
  createdAt: string;
  revokedAt: string | null;
}

export interface AuditLogViewModel {
  id: string;
  clientId: string;
  action: string;
  actorType: string;
  actorId: string | null;
  resourceType: string;
  resourceId: string;
  createdAt: string;
}

export interface EmployeeViewModel {
  id: string;
  employeeCode: string;
  clientId: string;
  name: string;
  email: string;
  phone: string;
  status: ApiEmployee['employee_status'];
  isActive: boolean;
  isClockedIn: boolean;
  createdAt: string;
  hireDate: string;
}

export function formatUtcTimestamp(value?: string | null): string {
  if (!value) {
    return 'N/A';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'N/A';
  }

  return date.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function deviceStatusToLabel(status: BackendDeviceStatus): string {
  switch (status) {
    case 'ACTIVE':
      return 'Active';
    case 'PROVISIONED':
      return 'Provisioned';
    case 'SUSPENDED':
      return 'Suspended';
    case 'REVOKED':
      return 'Revoked';
    default:
      return status;
  }
}

export function toClientViewModel(client: ApiClient): ClientViewModel {
  return {
    id: client.id,
    name: client.name,
  };
}

export function toDeviceViewModel(device: ApiDevice): DeviceViewModel {
  return {
    id: device.id,
    deviceId: device.device_id,
    clientId: device.client_id,
    status: device.status,
    statusLabel: deviceStatusToLabel(device.status),
    createdAt: formatUtcTimestamp(device.created_at),
    revokedAt: device.revoked_at ? formatUtcTimestamp(device.revoked_at) : null,
  };
}

export function toAuditLogViewModel(log: ApiAuditLog): AuditLogViewModel {
  return {
    id: log.id,
    clientId: log.client_id,
    action: log.action,
    actorType: log.actor_type,
    actorId: log.actor_id || null,
    resourceType: log.resource_type,
    resourceId: log.resource_id,
    createdAt: formatUtcTimestamp(log.created_at),
  };
}

export function toEmployeeViewModel(employee: ApiEmployee): EmployeeViewModel {
  return {
    id: employee.id,
    employeeCode: employee.employee_code,
    clientId: employee.client_id,
    name: `${employee.first_name} ${employee.last_name}`.trim(),
    email: employee.email || 'Unavailable',
    phone: employee.phone || 'Unavailable',
    status: employee.employee_status,
    isActive: employee.is_active,
    isClockedIn: employee.is_clocked_in,
    createdAt: formatUtcTimestamp(employee.created_at),
    hireDate: formatUtcTimestamp(employee.hire_date),
  };
}
